<template>
  <div
    class="dashboard-container min-h-full rounded-lg"
    :class="isCliente ? 'dashboard-cliente scrollbar-thin scrollbar-orange' : ''"
  >

    <!-- Client Dashboard (for users of type 'cliente') -->
    <ClientDashboard v-if="isCliente" />

    <!-- Staff Dashboard -->
    <template v-else>
    <div class="dashboard-header flex justify-end items-center pb-1 text-[var(--text)]">

      <button style="background-color: var(--fundoComponente);"v-if="this.validPermission('H0006') && user.tipo.nome !='cliente'" class="btn" @click="showSignature = true">
        <i class="fa-solid fa-file-signature fa-2xl" style="color: var(--iconCima)"></i>
        <div style="white-space: nowrap; color: var(--label);">Assinatura</div>
      </button>
    </div>

    <!-- ────────── New dashboard widgets ────────── -->
    <div class="db-widgets">
      <!-- Hero row: greeting / clock / weather -->
      <div class="db-hero">
        <div class="db-card db-greeting">
          <div class="db-eyebrow">{{ greetingPrefix }}</div>
          <div class="db-title">{{ greetingMessage }}, {{ userFirstName }}</div>
          <div class="db-subtitle">{{ formattedDate }}</div>
        </div>

        <div class="db-card db-clock">
          <div class="db-eyebrow">Hora</div>
          <div class="db-clock-time">
            {{ clockHM }}<span class="db-clock-seconds">:{{ clockS }}</span>
          </div>
          <div class="db-subtitle">{{ tzLabel }}</div>
        </div>

        <div class="db-card db-weather">
          <div class="db-eyebrow">Meteorologia</div>
          <template v-if="weather.loading">
            <div class="db-skel db-skel-line wide"></div>
            <div class="db-skel db-skel-line"></div>
          </template>
          <template v-else-if="weather.error">
            <div class="db-title small">—</div>
            <div class="db-subtitle">{{ weather.error }}</div>
          </template>
          <template v-else>
            <div class="db-weather-row">
              <i :class="['fa-solid', weather.icon, 'db-weather-icon']"></i>
              <div class="db-weather-temp">{{ weather.temperature }}°</div>
            </div>
            <div class="db-subtitle">{{ weather.description }} · {{ weather.location }}</div>
          </template>
        </div>
      </div>

      <!-- Counters -->
      <div class="db-counters">
        <div
          v-for="c in counterCards"
          :key="c.key"
          class="db-counter"
          :style="{ borderLeftColor: c.color }"
        >
          <div class="db-counter-icon" :style="{ color: c.color }">
            <i :class="c.icon"></i>
          </div>
          <div class="db-counter-body">
            <div class="db-counter-label">{{ c.label }}</div>
            <div class="db-counter-value">
              <span v-if="dashLoading" class="db-skel db-skel-num"></span>
              <span v-else>{{ formatNumber(c.value) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts -->
      <div class="db-charts">
        <div class="db-chart-card">
          <div class="db-chart-header">
            <h3>Intervenções por Técnico (hoje)</h3>
            <span class="db-chart-meta">{{ totalIntervencoesHoje }} no total</span>
          </div>
          <div class="db-chart-body">
            <div v-if="dashLoading" class="db-skel db-skel-block"></div>
            <Bar
              v-else-if="tecnicoChartData.labels.length"
              :data="tecnicoChartData"
              :options="tecnicoChartOptions"
            />
            <div v-else class="db-empty">Sem intervenções para hoje.</div>
          </div>
        </div>

        <div class="db-chart-card">
          <div class="db-chart-header">
            <h3>Negócios por Etapa</h3>
            <span class="db-chart-meta">{{ totalNegocios }} ativos</span>
          </div>
          <div class="db-chart-body">
            <div v-if="dashLoading" class="db-skel db-skel-block"></div>
            <Doughnut
              v-else-if="etapasChartData.labels.length"
              :data="etapasChartData"
              :options="etapasChartOptions"
            />
            <div v-else class="db-empty">Sem negócios.</div>
          </div>
        </div>

        <div class="db-chart-card">
          <div class="db-chart-header">
            <h3>Estado das Ordens de Serviço</h3>
            <span class="db-chart-meta">{{ totalOSEstado }} no total</span>
          </div>
          <div class="db-chart-body">
            <div v-if="dashLoading" class="db-skel db-skel-block"></div>
            <Bar
              v-else-if="osStatusChartData.labels.length"
              :data="osStatusChartData"
              :options="osStatusChartOptions"
            />
            <div v-else class="db-empty">Sem dados.</div>
          </div>
        </div>
      </div>
    </div>

    
    </template>
  </div>
  <Signature v-if="!isCliente && showSignature" :initial-signature="assinatura" @close="showSignature = false"
    @save="onSaveSignature" />
</template>

<script>
import AppProfileSearch from '@/components/profileSearch.vue';
import AppNotification from '@/components/notification.vue';
import AppActionCard from '@/components/actionCard.vue';
import AppPerfilForm from '@/components/perfil/perfilForm.vue';
import AppTable from '@/components/tables/table.vue';
import AppFilterForm from '@/components/filter/filterForm.vue';
import axios from 'axios';
import permissionMixin from '@/permissionMixin.js';
import Task from '@/components/task/main.vue';
import GeneralChat from '@/views/chatGrupoView.vue';
import PrivateChat from '@/views/chatPrivadoView.vue';
import Signature from '@/components/signature/signature.vue';
import ClientDashboard from '@/views/Dashboard/ClientDashboardView.vue';
import { toast } from 'vue3-toastify';

import { Bar, Doughnut } from 'vue-chartjs';
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
} from 'chart.js';

ChartJS.register(Title, Tooltip, Legend, ArcElement, CategoryScale, LinearScale, BarElement);

import { id as loggedID } from '@/extraData'
import { useTheme } from '@/composables/useTheme'

// Mapa simples de WMO weather code (Open-Meteo) → ícone FontAwesome + descrição
const WMO_MAP = {
  0:  { icon: 'fa-sun',          desc: 'Céu limpo' },
  1:  { icon: 'fa-sun',          desc: 'Maioritariamente limpo' },
  2:  { icon: 'fa-cloud-sun',    desc: 'Parcialmente nublado' },
  3:  { icon: 'fa-cloud',        desc: 'Nublado' },
  45: { icon: 'fa-smog',         desc: 'Nevoeiro' },
  48: { icon: 'fa-smog',         desc: 'Nevoeiro com geada' },
  51: { icon: 'fa-cloud-rain',   desc: 'Chuvisco fraco' },
  53: { icon: 'fa-cloud-rain',   desc: 'Chuvisco' },
  55: { icon: 'fa-cloud-rain',   desc: 'Chuvisco intenso' },
  61: { icon: 'fa-cloud-showers-heavy', desc: 'Chuva fraca' },
  63: { icon: 'fa-cloud-showers-heavy', desc: 'Chuva' },
  65: { icon: 'fa-cloud-showers-heavy', desc: 'Chuva intensa' },
  71: { icon: 'fa-snowflake',    desc: 'Neve fraca' },
  73: { icon: 'fa-snowflake',    desc: 'Neve' },
  75: { icon: 'fa-snowflake',    desc: 'Neve intensa' },
  80: { icon: 'fa-cloud-showers-heavy', desc: 'Aguaceiros' },
  81: { icon: 'fa-cloud-showers-heavy', desc: 'Aguaceiros' },
  82: { icon: 'fa-cloud-showers-heavy', desc: 'Aguaceiros fortes' },
  95: { icon: 'fa-cloud-bolt',   desc: 'Trovoada' },
  96: { icon: 'fa-cloud-bolt',   desc: 'Trovoada com granizo' },
  99: { icon: 'fa-cloud-bolt',   desc: 'Trovoada forte' },
};

export default {
  mixins: [permissionMixin],
  name: 'AppDashboard',
  setup() {
    const { theme } = useTheme();
    return { theme };
  },
  components: {
    AppActionCard,
    AppPerfilForm,
    AppTable,
    AppProfileSearch,
    AppNotification,
    AppFilterForm,
    Task,
    GeneralChat,
    PrivateChat,
    Signature,
    ClientDashboard,
    Bar,
    Doughnut,
  },
  data() {
    return {
      baseURl: process.env.VUE_APP_URL_BASE,
      hasPermission: false,
      hasPermissions: false,
      showT: false,
      showSignature: false,
      user: {},

      // Dashboard widgets
      now: new Date(),
      _clockInterval: null,
      tzLabel: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
      dashLoading: true,
      dashData: {
        counters: {},
        intervencoes_por_tecnico: [],
        negocios_por_etapa: [],
        os_por_status: [],
      },
      weather: {
        loading: true,
        error: null,
        temperature: null,
        description: '',
        location: '',
        icon: 'fa-cloud',
      },
      filtros: {},
      filtersByTema: [],
      filter: [],
      selectedTema: 'Escolha o Tema',
      selectedFilter: {},
      table_data: [],
      genericData: [],
      genericTitle: '',
      tabela: '',
      filtersByTabela: {},
      firstFilters: {},
      assinatura: '',
      items: {
        Roles: 'rol',
        Utilizadores: 'utilizador',
        Clientes: 'cliente',
        Fornecedores: 'fornecedor',
        Armazens: 'armazem',
        Viaturas: 'viatura',
        Familias: 'familia',
        Equipamentos: 'equipamento',
        Artigos: 'artigo',
        Serviços: 'servico',
        Planeamento: 'planeamento',
        'Tabela-Preço': 'tabela_preco',
        Contactos: 'contacto',
      },
      showGeneralChat: false,
      showPrivateChat: false,

    };
  },



  computed: {
    // Paleta dos gráficos reativa ao tema (Chart.js define cores em JS, não CSS)
    chartPalette() {
      const light = this.theme === 'light';
      return {
        tickMuted: light ? 'rgba(15,23,42,.6)' : '#94a3b8',
        tickStrong: light ? 'rgba(15,23,42,.75)' : '#cbd5e1',
        grid: light ? 'rgba(15,23,42,.1)' : 'rgba(255,255,255,.05)',
        legend: light ? 'rgba(15,23,42,.8)' : '#cbd5e1',
      };
    },
    isCliente() {
      return this.user?.tipo?.nome === 'cliente';
    },
    userFirstName() {
      const raw = this.user?.user || this.user?.nome || '';
      if (!raw) return 'Bem-vindo(a)';
      return String(raw).split(/[\s.]+/)[0];
    },
    greetingPrefix() {
      const h = this.now.getHours();
      if (h < 6) return 'Madrugada';
      if (h < 12) return 'Manhã';
      if (h < 19) return 'Tarde';
      return 'Noite';
    },
    greetingMessage() {
      const h = this.now.getHours();
      if (h < 12) return 'Bom dia';
      if (h < 19) return 'Boa tarde';
      return 'Boa noite';
    },
    formattedDate() {
      try {
        return this.now.toLocaleDateString('pt-PT', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
      } catch (e) {
        return this.now.toDateString();
      }
    },
    clockHM() {
      const h = String(this.now.getHours()).padStart(2, '0');
      const m = String(this.now.getMinutes()).padStart(2, '0');
      return `${h}:${m}`;
    },
    clockS() {
      return String(this.now.getSeconds()).padStart(2, '0');
    },
    counterCards() {
      const c = this.dashData?.counters || {};
      return [
        { key: 'clientes',                label: 'Clientes',                value: c.clientes ?? 0,                icon: 'fa-solid fa-handshake',     color: '#22c55e' },
        { key: 'locais_instalacao',       label: 'Locais Instalação',       value: c.locais_instalacao ?? 0,       icon: 'fa-solid fa-location-dot',  color: '#06b6d4' },
        { key: 'negocios_ativos',         label: 'Negócios Ativos',         value: c.negocios_ativos ?? 0,         icon: 'fa-solid fa-briefcase',     color: '#f59e0b' },
        { key: 'intervencoes_hoje',       label: 'Intervenções (hoje)',     value: c.intervencoes_hoje ?? 0,       icon: 'fa-solid fa-calendar-day',  color: '#3b82f6' },
        { key: 'intervencoes_confirmadas',label: 'Intervenções Confirmadas',value: c.intervencoes_confirmadas ?? 0,icon: 'fa-solid fa-circle-check',  color: '#10b981' },
        { key: 'intervencoes_fechadas_hoje', label: 'OS Fechadas (hoje)',   value: c.intervencoes_fechadas_hoje ?? 0, icon: 'fa-solid fa-flag-checkered', color: '#8b5cf6' },
        { key: 'fornecedores',            label: 'Fornecedores',            value: c.fornecedores ?? 0,            icon: 'fa-solid fa-truck-loading', color: '#ef4444' },
        { key: 'utilizadores',            label: 'Utilizadores',            value: c.utilizadores ?? 0,            icon: 'fa-solid fa-users',         color: '#7c3aed' },
        { key: 'equipamentos',            label: 'Equipamentos',            value: c.equipamentos ?? 0,            icon: 'fa-solid fa-tools',         color: '#0ea5e9' },
        { key: 'artigos',                 label: 'Artigos',                 value: c.artigos ?? 0,                 icon: 'fa-solid fa-newspaper',     color: '#f97316' },
      ];
    },
    totalIntervencoesHoje() {
      return (this.dashData?.intervencoes_por_tecnico || []).reduce((sum, t) => sum + (t.total || 0), 0);
    },
    totalNegocios() {
      return (this.dashData?.negocios_por_etapa || []).reduce((sum, t) => sum + (t.total || 0), 0);
    },
    totalOSEstado() {
      return (this.dashData?.os_por_status || []).reduce((sum, t) => sum + (t.total || 0), 0);
    },
    tecnicoChartData() {
      const arr = (this.dashData?.intervencoes_por_tecnico || []);
      return {
        labels: arr.map((t) => t.tecnico || '—'),
        datasets: [
          {
            label: 'Intervenções',
            data: arr.map((t) => t.total || 0),
            backgroundColor: '#3b82f6',
            borderRadius: 6,
            barPercentage: 0.7,
          },
        ],
      };
    },
    tecnicoChartOptions() {
      return {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 350 },
        plugins: { legend: { display: false } },
        scales: {
          x: { ticks: { color: this.chartPalette.tickMuted, precision: 0 }, grid: { color: this.chartPalette.grid } },
          y: { ticks: { color: this.chartPalette.tickStrong }, grid: { display: false } },
        },
      };
    },
    etapasChartData() {
      const arr = (this.dashData?.negocios_por_etapa || []).filter((e) => e.total > 0);
      return {
        labels: arr.map((e) => e.etapa),
        datasets: [
          {
            data: arr.map((e) => e.total),
            backgroundColor: arr.map((e) => e.cor),
            borderColor: 'rgba(0,0,0,.2)',
            borderWidth: 1,
          },
        ],
      };
    },
    etapasChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 350 },
        plugins: {
          legend: { position: 'right', labels: { color: this.chartPalette.legend, boxWidth: 12, font: { size: 11 } } },
        },
      };
    },
    osStatusChartData() {
      const arr = this.dashData?.os_por_status || [];
      const colors = {
        ativo: '#22c55e', pendente: '#f59e0b', confirmado: '#06b6d4', atribuido: '#8b5cf6',
        agendado: '#3b82f6', interrompido: '#f97316', fechado: '#10b981', cancelado: '#ef4444',
        remarcar: '#eab308', desativado: '#6b7280', execução: '#0ea5e9',
      };
      return {
        labels: arr.map((s) => s.status),
        datasets: [
          {
            label: 'OS',
            data: arr.map((s) => s.total || 0),
            backgroundColor: arr.map((s) => colors[s.status] || '#94a3b8'),
            borderRadius: 6,
            barPercentage: 0.7,
          },
        ],
      };
    },
    osStatusChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 350 },
        plugins: { legend: { display: false } },
        scales: {
          x: { ticks: { color: this.chartPalette.tickMuted }, grid: { display: false } },
          y: { ticks: { color: this.chartPalette.tickStrong, precision: 0 }, grid: { color: this.chartPalette.grid } },
        },
      };
    },
  },

  created() {
    this.user = JSON.parse(localStorage.getItem('user'));
    this.get_assinatura(this.user.id);

    // this.checkToken();
    const storedTableData = localStorage.getItem('table_data');
    if (storedTableData) {
      this.table_data = JSON.parse(storedTableData);
    }
  },

  mounted() {
    if (this.isCliente) return;

    // Relógio (atualiza ao segundo). Não bloqueante.
    this._clockInterval = setInterval(() => { this.now = new Date(); }, 1000);

    // Fetch em paralelo. Skeletons garantem que a UI já está pronta.
    this.loadDashboardData();
    this.loadWeather();
  },

  beforeUnmount() {
    if (this._clockInterval) clearInterval(this._clockInterval);
  },
  watch: {
    selectedTema: function (newVal) {
      let theme = newVal;
      let array = this.filtros;
      this.filtersByTema = array.filter((item) => item.tabela === theme);
    },
    selectedFilter: function (newVal, oldVal) {
      this.getData(newVal);
    },

    table_data: {
      handler(newTableData) {

        localStorage.setItem('table_data', JSON.stringify(newTableData));
      },
      deep: true,
    },
  },
  methods: {

    async getToken(userId) {
      try {
        const res = await axios.post(`${this.baseURl}/api/v1/check-token/${userId}/?tokenOnly=1`);
        return res.data?.data?.token ?? null;
      } catch (e) {
        console.error('Erro ao buscar usuário:', e);
        return null;
      }
    },

    async get_assinatura(id) {
      if (id == 0) return
      try {

        const response = await axios.get(`${this.baseURl}/api/v1/utilizador/assinatura/${id}/`, { responseType: 'blob' });
        // Create a local URL of the image
        this.assinatura = URL.createObjectURL(new Blob([response.data]));
      } catch (error) {
        console.log(error);
      }
    },

    async onSaveSignature(signatureBase64) {
      // 1) Fechamos o modal
      this.showSignature = false

      // const img = document.getElementById("signature_img");
      // img.src = signatureBase64

      // 3) Se precisar enviar ao servidor, converta a base64 em Blob antes
      //    Exemplo de conversão:
      const blob = this.dataURLToBlob(signatureBase64)
      const formData = new FormData()
      formData.append('foto', blob, 'signature.png')

      this.assinatura = signatureBase64
      await this.upload_signature(formData)
      // this.formData.newAssinatura = formData


    },

    dataURLToBlob(dataURL) {
      const arr = dataURL.split(',')
      const mime = arr[0].match(/:(.*?);/)[1]
      const bstr = atob(arr[1])
      let n = bstr.length
      const u8arr = new Uint8Array(n)
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n)
      }
      return new Blob([u8arr], { type: mime })
    },

    async upload_signature(assinatura) {
      if (assinatura) {

        try {
          const response = await axios.post(`${this.baseURl}/api/v1/utilizador/editar/assinatura/${this.user.id}/`, assinatura, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
          if (response) {
            toast.success("Assinatura atualizada com sucesso!", {
              position: toast.POSITION.TOP_CENTER,
              autoClose: 1000,
            });
            const filePath = response.data.filePath;
          }
          else {
            toast.error("Erro ao criar utilizador", {
              position: toast.POSITION.TOP_CENTER,
              autoClose: 1000,
            });
          }
          
          
          //this.formData.foto = filePath;
          //await this.get_assinatura(this.formData.id);

        } catch (error) {
          console.error("Error uploading file:", error);
        }
      }
    },

    async checkToken() {
      try {
        const userDataString = localStorage.getItem('user');
        if (userDataString) {
          const userData = JSON.parse(userDataString);
          if (userData && userData.token) {

            const tokenData = await this.getToken(userData.id);

            //if(tokenData.token !== userData.token ) {// && this.$route.name !== '/' && this.$route.name !== 'login'){

            if (tokenData && userData.token && tokenData == userData.token) {
              console.log('------------------------------------------------------------');
              console.log('Token Validado com sucesso!');
              console.log('------------------------------------------------------------');
            } else {
              console.log('------------------------------------------------------------');
              console.log('Token Inválido!');
              console.log('------------------------------------------------------------');
            }

            console.log('------------------------------------------------------------');
            console.log('Teste do TOKEN.');
            console.log('Token do LocalStorage:', userData.token);
            console.log('Token do Banco de Dados:', tokenData);
            console.log('------------------------------------------------------------');
            // localStorage.removeItem('user');
            // this.$router.push({name:'login'});
            // }


            config.headers['Authorization'] = `Bearer ${userData.token}`;
          }
        }
      } catch (e) {
        console.error('Erro no Interceptor:', e); // Debug
      }
    },



    getFilters() {
      return new Promise(async (resolve, reject) => {
        try {
          const response = await axios.get(
            `${this.baseURl}/api/v1/filtro/s/utilizador_id/${this.user.id}/?exat=true/`
          );
          this.filtros = response.data.data;

          let filtersByTabela = this.groupFiltersByTabela(this.filtros);
          this.filtersByTabela = filtersByTabela;
          resolve();
        } catch (error) {
          console.log(error);
          reject(error);
        }
      });
    },
    async getData(filter) {
      let filterObject = {};
      if (filter) {
        filterObject = {
          nome: filter.nome,
          utilizador_id: filter.utilizador_id,
          tabela: filter.tabela,
          campo: filter.campo.split('-'),
          stringSql: filter.stringSql,
        };
      }
      try {
        const response = await axios.post(
          `${this.baseURl}/api/v1/filtro/testar/`,
          filterObject
        );
        const tableDataWithNome = response.data.data.map((item) => ({
          ...item,
          nome: filter.nome,
        }));
        this.table_data.push({
          tabela: filter.tabela,
          filterName: filter.nome,
          table_data: response.data.data,
        });

        localStorage.setItem('table_data', JSON.stringify(this.table_data));
      } catch (error) {
        console.log(error);
      }
    },
    showTable(table) {
      this.genericData = table.table_data;
      this.genericTitle = table.tabela;
      this.showT = true;
    },
    groupFiltersByTabela(filtros) {
      return filtros.reduce((acc, filtro) => {
        if (!acc[filtro.tabela]) {
          acc[filtro.tabela] = [];
        }
        acc[filtro.tabela].push(filtro);
        return acc;
      }, {});
    },
    closeSquare(table) {
      const index = this.table_data.findIndex((t) => t.tabela === table.tabela);
      if (index !== -1) {
        this.table_data.splice(index, 1);
      }
    },
    closeTable() {
      this.showT = false;
    },

    toggleGeneralChat() {
      this.showGeneralChat = !this.showGeneralChat;
    },
    togglePrivateChat() {
      this.showPrivateChat = !this.showPrivateChat;
    },

    formatNumber(n) {
      try { return Number(n || 0).toLocaleString('pt-PT'); }
      catch (e) { return String(n ?? 0); }
    },

    async loadDashboardData() {
      try {
        const res = await axios.get(`${this.baseURl}/api/v1/dashboard/staff/`);
        const payload = res.data?.data || {};
        this.dashData = {
          counters: payload.counters || {},
          intervencoes_por_tecnico: payload.intervencoes_por_tecnico || [],
          negocios_por_etapa: payload.negocios_por_etapa || [],
          os_por_status: payload.os_por_status || [],
        };
      } catch (e) {
        console.warn('Dashboard load error:', e?.message || e);
      } finally {
        this.dashLoading = false;
      }
    },

    async loadWeather() {
      // Não bloqueia o resto da página: erros silenciosos para o utilizador,
      // só mostram um placeholder no card.
      const fail = (msg) => {
        this.weather = { ...this.weather, loading: false, error: msg };
      };

      if (!('geolocation' in navigator)) {
        return fail('Sem geolocalização');
      }

      const getPos = () => new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false,
          timeout: 6000,
          maximumAge: 10 * 60 * 1000, // cache 10 min
        });
      });

      try {
        const pos = await getPos();
        const lat = pos.coords.latitude;
        const lon = pos.coords.longitude;

        const wxUrl = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current_weather=true`;
        const geoUrl = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=pt`;

        // Em paralelo — meteorologia e nome do local
        const [wxRes, geoRes] = await Promise.allSettled([
          fetch(wxUrl).then((r) => r.json()),
          fetch(geoUrl).then((r) => r.json()),
        ]);

        if (wxRes.status !== 'fulfilled' || !wxRes.value?.current_weather) {
          return fail('Sem dados meteorológicos');
        }

        const cw = wxRes.value.current_weather;
        const code = cw.weathercode;
        const map = WMO_MAP[code] || { icon: 'fa-cloud', desc: 'Tempo indeterminado' };

        let location = '—';
        if (geoRes.status === 'fulfilled' && geoRes.value) {
          const g = geoRes.value;
          location = g.city || g.locality || g.principalSubdivision || g.countryName || '—';
        }

        this.weather = {
          loading: false,
          error: null,
          temperature: Math.round(cw.temperature),
          description: map.desc,
          location,
          icon: map.icon,
        };
      } catch (e) {
        const msg = (e && e.code === 1) ? 'Permissão negada' : 'Indisponível';
        fail(msg);
      }
    },
  },
};
</script>

<style scoped>
.dashboard-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* ── Em md/lg o dashboard ocupa o ecrã todo, sem scroll global ── */
@media (min-width: 768px) {
  .dashboard-container {
    height: calc(100vh - 36px); /* desconta o padding do HomeView */
    overflow: hidden;
  }
}

/* O dashboard de cliente (com o calendário) é mais alto do que o ecrã:
   é esta a caixa que faz o scroll da página, em qualquer tamanho. */
.dashboard-container.dashboard-cliente {
  overflow-y: auto;
  overflow-x: hidden;
}

.dashboard-header {}

.dashboard-content {
  flex: 1;
  display: flex;
}

/* A área antiga (chats + tabelas filtradas) só aparece em mobile;
   em md/lg o novo dashboard ocupa o ecrã todo. */
@media (min-width: 768px) {
  .hidden-md-up { display: none !important; }
}

.left-column {}

.right-column {}

.chat-section {
  display: flex;
  gap: 10px;
}

.chat-panel {
  background: #2f284a;
  border-radius: 10px;
  padding: 10px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.glass-app.theme-light .chat-panel {
  background: #e9edf4;
  border: 1px solid rgba(15, 23, 42, 0.12);
}

.table-responsive {
  display: block;
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

/* ────────── Dashboard Widgets ────────── */
.db-widgets {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  color: #e5e7eb;
}

/* Em md/lg, os widgets crescem para preencher todo o espaço útil */
@media (min-width: 768px) {
  .db-widgets {
    flex: 1 1 auto;
    min-height: 0;
    margin-bottom: 0;
    display: grid;
    grid-template-rows: auto auto 1fr;
  }
}

/* Hero (greeting / clock / weather) */
.db-hero {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.65rem;
}
@media (min-width: 768px) {
  .db-hero { grid-template-columns: 1.3fr 1fr 1fr; }
}

.db-card {
  background: rgba(78, 78, 130, 0.45);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  padding: 0.85rem 1rem;
  min-height: 90px;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.db-eyebrow {
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #94a3b8;
  font-weight: 700;
}
.db-title {
  font-size: 1.15rem;
  font-weight: 800;
  color: #f1f5f9;
  letter-spacing: -0.01em;
}
.db-title.small { font-size: 0.95rem; }
.db-subtitle {
  font-size: 0.78rem;
  color: #cbd5e1;
  text-transform: capitalize;
}

.db-clock-time {
  font-family: 'JetBrains Mono', ui-monospace, Menlo, monospace;
  font-size: 1.9rem;
  font-weight: 800;
  color: #f8fafc;
  line-height: 1;
}
.db-clock-seconds {
  font-size: 1.1rem;
  color: #fbbf24;
  margin-left: 2px;
}

.db-weather-row {
  display: flex;
  align-items: center;
  gap: 0.7rem;
}
.db-weather-icon { font-size: 1.6rem; color: #fbbf24; }
.db-weather-temp {
  font-size: 1.7rem;
  font-weight: 800;
  color: #f1f5f9;
  line-height: 1;
}

/* Counters */
.db-counters {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem;
}
@media (min-width: 640px) { .db-counters { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
@media (min-width: 1024px) { .db-counters { grid-template-columns: repeat(5, minmax(0, 1fr)); } }

.db-counter {
  background: rgba(78, 78, 130, 0.45);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-left: 3px solid #94a3b8;
  border-radius: 10px;
  padding: 0.6rem 0.7rem;
  display: flex;
  align-items: center;
  gap: 0.55rem;
  min-height: 64px;
  transition: transform .12s ease, background .12s ease;
}
.db-counter:hover {
  background: rgba(93, 93, 152, 0.55);
  transform: translateY(-1px);
}
.db-counter-icon {
  font-size: 1.05rem;
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.06);
  flex: 0 0 auto;
}
.db-counter-body { min-width: 0; }
.db-counter-label {
  font-size: 0.66rem;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.db-counter-value {
  font-size: 1.15rem;
  font-weight: 800;
  color: #f1f5f9;
  line-height: 1.2;
}

/* Charts grid */
.db-charts {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.65rem;
}
@media (min-width: 768px)  { .db-charts { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (min-width: 1024px) { .db-charts { grid-template-columns: repeat(3, minmax(0, 1fr)); } }

/* Em md/lg cada gráfico expande para preencher a linha 1fr do grid pai */
@media (min-width: 768px) {
  .db-charts {
    height: 100%;
    min-height: 0;
    grid-auto-rows: 1fr;
  }
  .db-chart-card {
    min-height: 0;
    overflow: hidden;
  }
}

.db-chart-card {
  background: rgba(78, 78, 130, 0.45);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  padding: 0.7rem 0.85rem 0.85rem;
  display: flex;
  flex-direction: column;
}
.db-chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  gap: 0.5rem;
  flex: 0 0 auto;
}
.db-chart-header h3 {
  font-size: 0.82rem;
  font-weight: 700;
  color: #e2e8f0;
  margin: 0;
}
.db-chart-meta {
  font-size: 0.7rem;
  color: #94a3b8;
}
.db-chart-body {
  flex: 1 1 auto;
  height: 240px;
  min-height: 200px;
  position: relative;
}

/* Em md/lg o gráfico cresce com o card */
@media (min-width: 768px) {
  .db-chart-body {
    height: auto;
  }
}

.db-empty {
  height: 100%;
  display: grid;
  place-items: center;
  color: #94a3b8;
  font-size: 0.82rem;
  font-style: italic;
}

/* ── Tema claro: cards a branco com texto escuro ── */
.glass-app.theme-light .db-card,
.glass-app.theme-light .db-counter,
.glass-app.theme-light .db-chart-card {
  background: #e9edf4;
  border-color: rgba(15, 23, 42, 0.12);
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.07);
}
.glass-app.theme-light .db-counter:hover {
  background: #dfe4ec;
}
.glass-app.theme-light .db-counter-icon {
  background: rgba(15, 23, 42, 0.05);
}
.glass-app.theme-light .db-title,
.glass-app.theme-light .db-clock-time,
.glass-app.theme-light .db-weather-temp,
.glass-app.theme-light .db-counter-value,
.glass-app.theme-light .db-chart-header h3 {
  color: #10192e;
}
.glass-app.theme-light .db-eyebrow,
.glass-app.theme-light .db-subtitle,
.glass-app.theme-light .db-counter-label,
.glass-app.theme-light .db-chart-meta,
.glass-app.theme-light .db-empty {
  color: rgba(15, 23, 42, 0.6);
}
.glass-app.theme-light .db-weather-icon,
.glass-app.theme-light .db-clock-seconds {
  color: #d97706;
}

/* Skeleton placeholders (não bloqueiam o layout) */
.db-skel {
  background: linear-gradient(90deg, rgba(255,255,255,.04), rgba(255,255,255,.10), rgba(255,255,255,.04));
  background-size: 200% 100%;
  animation: db-shimmer 1.2s infinite;
  border-radius: 6px;
}
.db-skel-num   { display: inline-block; width: 56px; height: 22px; }
.db-skel-line  { height: 12px; margin-top: 6px; width: 70%; }
.db-skel-line.wide { width: 90%; }
.db-skel-block { width: 100%; height: 100%; min-height: 200px; }
@keyframes db-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
</style>
